import { useParams } from '@tanstack/react-router';
import { Empty, Link, Page, Rule, ui } from '../ui';
import type { CreatorPageData } from '@api/views';
import { useApi } from '../api';
import { Loading, NotFound } from './shared';
import { useDocumentTitle } from '../title';

/**
 * The public profile page. One, where there were three (#144).
 *
 * A creator's, painted in the creators' ground while keeping the store's
 * chrome. A studio's and a publisher's were beside it; see the note below.
 *
 * All three were `.astro` pages on the storefront and read the database
 * in-process. This one reads `/v1/catalogue/creators/:handle`, which is the
 * endpoint that already existed for exactly this reason — a client that is not
 * the storefront.
 */

/*
  `DeveloperPageRoute` and `PublisherPageRoute` were here, and went with the
  studios (#144).

  A developer page was section 6.1: the studio's own "about us" beside a
  generated block of what players had found across its whole catalogue — the
  same duality a game page has, with the second half neither editable nor
  suppressible. A publisher page was the company above it.

  Both only mean something when the catalogue being aggregated belongs to
  somebody in particular. There is one catalogue and it is ours, so a
  credibility block about it is a credibility block about the store, and a page
  where we grade ourselves is not the same kind of thing at all.

  A title carries its maker's name as a string now, printed on the game page and
  not linked. `/dev/:slug` and `/pub/:slug` are gone from the router and
  `/v1/catalogue/developers/:slug` from the API.
*/

/**
 * Section 12. A creator page is a real storefront, not a profile.
 *
 * Public, works without an account, and reachable at `goodbuild.cc/@handle` as
 * well as here — see `NotHere` in `router.tsx`, which is where that vanity form
 * is resolved now that there is no Astro catch-all to rewrite it.
 *
 * Every buy link on this page carries attribution: `?c=` is the creator's
 * handle, which is attribution path 3 — and arriving here at all records a
 * `creator_page` touch, which is path 2.
 *
 * **Store chrome, creators ground.** The page belongs to the store because it
 * is where a *player* reads what a creator recommends, but section 04 makes any
 * creator surface Hot throughout. So it takes the store's navigation and the
 * creators' colour, which is what `space` on the page below switches.
 */
export function CreatorPageRoute({ handle: given }: { handle?: string }): React.ReactElement {
  const params = useParams({ strict: false }) as { handle?: string };
  const handle = given ?? params.handle ?? '';

  const query = useApi<CreatorPageData>(
    ['creator', handle],
    `/creators/${encodeURIComponent(handle)}`,
    {
      enabled: handle !== '',
    },
  );

  const creator = query.data;
  useDocumentTitle(creator?.displayName ?? null);

  if (query.isPending) return <Waiting space="creators" />;
  if (query.error?.status === 404 || !creator) return <NotFound />;

  const shelved = creator.shelves.reduce((total, entry) => total + entry.items.length, 0);

  return (
    <Page site="store" space="creators">
      <header className={ui.stack ?? ''}>
        <p className={ui.statLabel ?? ''} style={{ color: 'var(--gb-accent)' }}>
          Creator
        </p>
        <h1 className="gb-headline">{creator.displayName}</h1>
        {/* The muted role, not a fixed ramp value: #4a4a57 is unreadable on Ink. */}
        <p className={ui.numeric ?? ''} style={{ color: 'var(--gb-text-muted)', margin: 0 }}>
          @{creator.handle}
          {creator.primaryLanguage ? ` · ${creator.primaryLanguage}` : ''}
        </p>
        {creator.bio ? <p className="gb-body">{creator.bio}</p> : null}
      </header>

      <Rule />

      {creator.shelves.length === 0 || shelved === 0 ? (
        <Empty>Nothing picked out yet.</Empty>
      ) : (
        creator.shelves.map((entry) => (
          <section key={entry.id} className={ui.stack ?? ''}>
            <h2 className={ui.sectionTitle ?? ''}>{entry.title}</h2>
            {entry.description ? (
              <p className="gb-body" style={{ margin: 0 }}>
                {entry.description}
              </p>
            ) : null}
            <div className={ui.grid ?? ''}>
              {entry.items.map((item) => (
                <article key={item.productSlug} className={ui.card ?? ''}>
                  <h3 className={ui.cardTitle ?? ''}>
                    {/* Attribution rides the link: ?c= is this creator's code. */}
                    <Link href={`/g/${item.productSlug}?c=${creator.handle}`}>
                      {item.productName}
                    </Link>
                  </h3>
                  <p className={ui.cardMeta ?? ''}>{item.developerName}</p>
                  {item.note ? (
                    <p className="gb-body" style={{ margin: 0, fontSize: '0.9375rem' }}>
                      {item.note}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ))
      )}
    </Page>
  );
}

/**
 * What all three draw while the request is out.
 *
 * The chrome first and the spinner inside it, rather than a bare loader on an
 * unstyled page: these are pages somebody arrives at from outside, so the first
 * frame is the first thing they see of the product.
 */
function Waiting({ space }: { space?: 'creators' }): React.ReactElement {
  return (
    <Page site="store" {...(space ? { space } : {})}>
      <Loading />
    </Page>
  );
}
